import Button from 'components/Button';
import IconLink from 'components/IconLink';
import Img from 'components/Img';
import Text from 'components/Text';
import {
	useByteFormatter,
	useLengthFormatter,
	useWeightFormatter,
} from 'hooks/format';
import type {
	PackageSize,
	TechnicalAttribute,
	TechnicalAttributeCategory,
} from 'models/api';
import type { AssetResponse } from 'models/asset';
import { cn } from 'utils/classNames';
import { filterTruthy } from 'utils/collection';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

const sortByIndex = (
	a: TechnicalAttributeCategory | TechnicalAttribute,
	b: TechnicalAttributeCategory | TechnicalAttribute,
) => (a.index ?? 0) - (b.index ?? 0);

interface TableProps {
	rows: { id: string; title: string; value: string | number }[];
	title: string;
}
function Table({ rows, title }: TableProps) {
	const cellClasses = 'border-b border-t border-subtle px-0 py-3 text-sm';

	return (
		<table className="w-full">
			<Text as="caption" styleAs="h4" className="pb-2 text-left">
				{title}
			</Text>
			<tbody>
				{rows.map((row) => (
					<tr key={row.id}>
						<th
							scope="row"
							className={cn(cellClasses, 'text-left font-normal')}
						>
							{row.title}
						</th>
						<td className={cn(cellClasses, 'text-right')}>{row.value}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}
Table.displayName = 'TechnicalSpecification_Table';

interface BaseProps {
	/** Groups of technical data values. */
	categories?: TechnicalAttributeCategory[];

	/** Container class names. */
	className?: string;

	/** List of documents for download. */
	documents?: AssetResponse[];

	/** Heading for the documents section. */
	documentsHeading: string;

	energyLabelAlt: string | undefined;

	/** Energy label image URL. */
	energyLabelSrc?: string;

	/** Button text for manufacturer information. */
	manufacturerInformationButtonText?: string;

	/** Heading for the manufacturer information section. */
	manufacturerInformationHeading?: string;

	/** Callback to open the manufacturer information popover. */
	onManufacturerInformationClick?: () => void;

	/** Product packaging information. */
	packageSize?: PackageSize;

	/** Heading for the package size section. */
	packageSizeHeading: string;
}

interface PropsWithoutManufacturer extends BaseProps {
	hasManufacturerInfo?: never;
	manufacturerInformationButtonText?: never;
	manufacturerInformationHeading?: never;
	onManufacturerInformationClick?: never;
}

interface PropsWithManufacturer extends BaseProps {
	hasManufacturerInfo: boolean;
	manufacturerInformationButtonText: string;
	manufacturerInformationHeading: string;
	onManufacturerInformationClick: () => void;
}

type Props = PropsWithoutManufacturer | PropsWithManufacturer;

/** Component for table of a product's technical specifications. */
export default function TechnicalSpecification({
	categories,
	className,
	documents: documentsProp,
	documentsHeading,
	energyLabelAlt,
	energyLabelSrc,
	hasManufacturerInfo,
	manufacturerInformationButtonText,
	manufacturerInformationHeading,
	onManufacturerInformationClick,
	packageSize,
	packageSizeHeading,
}: Props) {
	const { t } = useI18n();
	const formatLength = useLengthFormatter('centimeter');
	const formatWeight = useWeightFormatter();
	const formatBytes = useByteFormatter();
	const documents = filterTruthy(documentsProp, 'src', 'alt');

	return (
		<div className={cn(className, energyLabelSrc && 'md:flex md:items-start')}>
			<div className={cn('space-y-10', energyLabelSrc && 'md:grow')}>
				{is.arrayWithLength(categories) &&
					categories.toSorted(sortByIndex).map((category) => (
						<Table
							key={category.id}
							title={category.title}
							rows={category.attributes.toSorted(sortByIndex).map((attr) => ({
								id: attr.id,
								title: attr.title,
								value:
									attr.values.length === 0 ||
									(attr.values.length === 1 && attr.values[0] === '')
										? '—'
										: attr.values.join(', '),
							}))}
						/>
					))}

				{packageSize && (
					<Table
						title={packageSizeHeading}
						rows={[
							{
								id: 'width',
								title: t('product_details_package_size_width_title'),
								value: formatLength(packageSize.width),
							},
							{
								id: 'height',
								title: t('product_details_package_size_height_title'),
								value: formatLength(packageSize.height),
							},
							{
								id: 'depth',
								title: t('product_details_package_size_depth_title'),
								value: formatLength(packageSize.depth),
							},
							{
								id: 'weight',
								title: t('product_details_package_size_weight_title'),
								value: formatWeight(packageSize.freightWeight),
							},
						]}
					/>
				)}

				{is.arrayWithLength(documents) && (
					<div>
						<Text as="h4" className="mb-4">
							{documentsHeading}
						</Text>
						<ul>
							{documents.map((doc) => {
								const suffix = [formatBytes(doc.fileSize), doc.formatName]
									.filter(Boolean)
									.join(' - ');

								return (
									<li key={doc.src} className="mt-2">
										<IconLink
											icon="file"
											href={doc.src}
											target="_blank"
											rel="nofollow"
											text={suffix ? `${doc.alt} (${suffix})` : doc.alt}
											className="align-middle"
										/>
									</li>
								);
							})}
						</ul>
					</div>
				)}

				{hasManufacturerInfo && onManufacturerInformationClick && (
					<div>
						<Text
							as="h4"
							className="mb-2"
							text={manufacturerInformationHeading}
						/>
						<Button
							hasExpandedHitArea
							variant="text"
							onClick={onManufacturerInformationClick}
						>
							{manufacturerInformationButtonText}
						</Button>
					</div>
				)}
			</div>

			{energyLabelSrc && (
				<Img
					width={230}
					height={500}
					src={energyLabelSrc}
					alt={energyLabelAlt}
					className="max-w-[230px] max-md:mt-10 md:ml-10 md:shrink-0"
				/>
			)}
		</div>
	);
}
TechnicalSpecification.displayName = 'TechnicalSpecification';
